import { selectInputWhenFocussed } from '@/utils/selectInputWhenFocussed';
import clsx from 'clsx';
import { InputHTMLAttributes, PropsWithChildren, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Radio } from 'ui/icons';

type IProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'title'> & {
  icon?: React.ReactNode;
  radioIcon?: React.ComponentType<{ selected?: boolean }>;
  title: React.ReactNode;
  description?: React.ReactNode;
  selected?: boolean;
};

const SelectRadio = forwardRef<HTMLInputElement, PropsWithChildren<IProps>>(
  (props, ref) => {
    const {
      description,
      title,
      icon = null,
      selected,
      radioIcon: RadioIcon,
      children,
      className,
      ...rest
    } = props;
    return (
      <label
        htmlFor={rest.id}
        tabIndex={0}
        onKeyUp={selectInputWhenFocussed(rest.id ?? '')}
        className={twMerge(
          className,
          clsx({
            'flex flex-col items-stretch w-full rounded-2xl border-mobile-gray-100 border bg-white gap-4 transition-colors':
              true,
            '!border-gray-500': selected || rest.checked,
            'cursor-not-allowed': rest.disabled,
            'cursor-pointer': !rest.disabled,
          })
        )}
      >
        <div
          className={clsx({
            'flex items-center w-full gap-4 transition-colors': true,
            '!border-gray-500': selected || rest.checked,
            'cursor-not-allowed': rest.disabled,
            'cursor-pointer': !rest.disabled,
          })}
        >
          <div className="w-full py-4 px-3 md:p-6 md:px-5  gap-3 md:gap-4  items-center flex">
            {icon ? <div className="flex-shrink-0 ">{icon}</div> : null}
            <div>
              <h3 className="text-mobile-text-main font-semibold leading-[22px] md:text-xl">
                {title}
              </h3>
              {description ? (
                <p className="mt-1 text-mobile-text-hierarchy-1 text-sm leading-5 md:text-base">
                  {description}
                </p>
              ) : null}
            </div>
          </div>
          <div className="py-4 px-3 md:p-6 md:px-5 flex">
            <input
              ref={ref}
              id={rest.id}
              name={rest.name}
              className="w-[1px] h-[1px] invisible"
              type="radio"
              checked={selected || rest.checked}
              {...rest}
            />
            {RadioIcon ? (
              <RadioIcon selected={selected || rest.checked} />
            ) : (
              <Radio isChecked={selected || rest.checked} />
            )}
          </div>
        </div>
        {!(selected || rest.checked) ? children : null}
      </label>
    );
  }
);

export default SelectRadio;
